import { List, Text, Title } from "@mantine/core";
import { Fragment, type ReactNode } from "react";
import type { RichText } from "@how/website-core";

type Inlines = Extract<RichText[number], { type: "paragraph" }>["content"];

function Runs({ runs }: { readonly runs: Inlines }): ReactNode {
  return runs.map((run, i) => {
    let node: ReactNode = run.text;
    if (run.bold === true) node = <strong>{node}</strong>;
    if (run.italic === true) node = <em>{node}</em>;
    if (run.href !== undefined) {
      node = (
        <a
          href={run.href}
          target={run.href.startsWith("/") ? undefined : "_blank"}
          rel="noreferrer"
        >
          {node}
        </a>
      );
    }
    return <Fragment key={i}>{node}</Fragment>;
  });
}

/**
 * Structured rich text (ADR 0033) drawn as elements, never markup injected: sermon notes and a devotional's reflection.
 */
export function RichTextView({ nodes }: { readonly nodes: RichText }) {
  return (
    <div>
      {nodes.map((node, i) => {
        const key = i;
        switch (node.type) {
          case "heading":
            return (
              <Title key={key} order={3} size="h5" mt="sm">
                <Runs runs={node.content} />
              </Title>
            );
          case "list":
            return (
              <List key={key} type={node.ordered ? "ordered" : "unordered"} mb="sm">
                {node.items.map((item, j) => (
                  <List.Item key={j}>
                    <Runs runs={item} />
                  </List.Item>
                ))}
              </List>
            );
          case "quote":
            return (
              <blockquote
                key={key}
                style={{
                  margin: "0 0 12px",
                  paddingLeft: 12,
                  borderLeft: "3px solid var(--how-border)",
                }}
              >
                <Runs runs={node.content} />
                {node.cite === undefined ? null : (
                  <Text size="sm" c="dimmed">
                    {node.cite}
                  </Text>
                )}
              </blockquote>
            );
          case "paragraph":
            return (
              <Text key={key} size="sm" mb="sm">
                <Runs runs={node.content} />
              </Text>
            );
        }
      })}
    </div>
  );
}
